<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import draggable from 'vuedraggable';
  import { ICaja } from '../../interfaces/ICaja';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import NewCajaModal from '../modals/NewCajaModal.vue';
  import AddCajasModal from '../modals/AddCajasModal.vue';
  import CajasService from '../../api/services/cajas.service';
  import RestaurantsService from '../../api/services/restaurants.service';
  import ConfirmModal from '../modals/ConfirmModal.vue';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { IBranch } from '../../interfaces/IBranch';
  import { PermissionType } from '../../enums/PermissionType';
  import { IUser } from '../../interfaces/IUser';
  import {
    b64toBlob,
    createQrBase64,
    createQrImage,
    importScript,
    loadImage,
    rotateCanvas,
  } from '../../utils';
  import ZebraUtils from '../../lib/print/ZebraUtils';
  import { Globals } from '../../modules/Globals';
  import { createBMP, drawArray } from '../../plugins/jsbmp';
  import InvoicingDataService from '@/api/services/invoicing/invoicing-data.service';
  import { IInvoicingData } from '@/interfaces/IInvoicingData';
  import InvoicingAfipService from '@/api/services/invoicing/invoicing-afip.service';
  import { ISalesPoint } from '@/interfaces/IAfip';
  import InvoicingSetupProgress from '@/components/restaurant/invoicing/InvoicingSetupProgress.vue';

  @Component({
    components: { draggable, InvoicingSetupProgress },
  })
  export default class RestaurantInvoicing extends Vue {
    @Prop() restaurant: IRestaurant;
    @Prop() user: IUser;
    @Prop() branch: IBranch;
    invoicingData: IInvoicingData = null;
    status = 'loading';
    creating = false;
    salesPoints: ISalesPoint[] = null;
    businessCategories = [
      { value: 'm', text: 'Monotributista' },
      { value: 'r', text: 'Responsable Inscripto' },
      // { value: 'e', text: 'Exento' },
    ];
    updating = false;
    removing = false;
    inicioActividadesMenu = false;

    setupData = {
      name: '',
      cuit: '',
      cuitAdministrador: '',
      password: '',
      businessCategory: 'r',
    };

    setupJobPublicId: string = null;

    selectedSalesPoint: ISalesPoint = null;

    async created() {}

    get fullyConfigured() {
      return !!this.invoicingData?.data?.nroSalesPoint;
    }

    get step() {
      if (!this.invoicingData) return 1;
      if (!this.invoicingData.data?.nroSalesPoint) return 2;
      return 3;
    }

    get autoInvoice() {
      return this.invoicingData?.data?.autoInvoice ?? false;
    }

    set autoInvoice(value: boolean) {
      if (!this.invoicingData) return;
      this.invoicingData.data.autoInvoice = value;
    }

    @Watch('branch', { immediate: true })
    onBranchChanged() {
      this.setupJobPublicId = null;
      this.loadInvoicingData();
    }

    /**
     * Vue 2 reactivity: new optional keys must exist on `data` before it
     * becomes reactive, otherwise v-model assignments won't trigger updates.
     */
    normalizeInvoicingData(invoicingData: IInvoicingData): IInvoicingData {
      if (invoicingData?.data) {
        if (invoicingData.data.ingresosBrutos === undefined) {
          invoicingData.data.ingresosBrutos = '';
        }
        if (invoicingData.data.inicioActividades === undefined) {
          invoicingData.data.inicioActividades = '';
        }
      }
      return invoicingData;
    }

    async loadInvoicingData() {
      try {
        this.status = 'loading';
        const invoicingData = await InvoicingDataService.getByBranch({
          restaurantId: this.restaurant.id,
          branchId: this.branch.id,
          platform: 'ar-afip',
        });

        this.invoicingData = this.normalizeInvoicingData(invoicingData);

        // Reattach to a running setup job (survives refresh/navigation).
        if (!this.invoicingData) {
          await this.reattachToActiveSetupJob();
        }
      } catch (error) {
        vueData.serverError(error, 'Error al cargar datos de facturacion');
        console.error(error);
      } finally {
        this.status = 'loaded';
      }
    }

    async reattachToActiveSetupJob() {
      try {
        const activeJob = await InvoicingAfipService.getActiveSetupJob(
          this.restaurant.id,
          this.branch.id
        );
        if (activeJob) this.setupJobPublicId = activeJob.publicId;
      } catch (error) {
        // Non-fatal: the user can simply start the setup again.
        console.error(error);
      }
    }

    async create() {
      try {
        this.creating = true;
        const { jobPublicId } = await InvoicingAfipService.startSetup({
          restaurantId: this.restaurant.id,
          branchId: this.branch.id,
          name: this.setupData.name,
          cuit: this.setupData.cuit,
          claveFiscal: this.setupData.password,
          cuitAdministrador: this.setupData.cuitAdministrador,
          businessCategory: this.setupData.businessCategory,
        });

        this.setupJobPublicId = jobPublicId;
      } catch (error) {
        // Another session already started one: reattach instead of failing.
        if (this.isSetupInProgressError(error)) {
          await this.reattachToActiveSetupJob();
          return;
        }
        vueData.serverError(
          error,
          'Error, verifique los datos e intente nuevamente'
        );
        console.error(error);
      } finally {
        this.creating = false;
      }
    }

    isSetupInProgressError(error: unknown): boolean {
      if (typeof error !== 'object' || error === null) return false;
      const response = (error as Record<string, unknown>).response;
      if (typeof response !== 'object' || response === null) return false;
      const data = (response as Record<string, unknown>).data;
      if (typeof data !== 'object' || data === null) return false;
      return (data as Record<string, unknown>).code === 'SETUP_IN_PROGRESS';
    }

    onSetupSucceeded(invoicingData: IInvoicingData | null) {
      this.setupJobPublicId = null;
      vueData.addAlert('Autorización creada correctamente', AlertType.Success);

      if (invoicingData) {
        this.invoicingData = this.normalizeInvoicingData(invoicingData);
      } else {
        this.loadInvoicingData();
      }
    }

    onSetupRetry() {
      this.setupJobPublicId = null;
    }

    async getSalesPoints() {
      try {
        this.salesPoints = null;

        const salesPoints = await InvoicingAfipService.getSalesPoints({
          restaurantId: this.restaurant.id,
          branchId: this.branch.id,
          invoicingDataId: this.invoicingData.id,
        });

        this.salesPoints = salesPoints.filter((sp) => sp.Bloqueado === 'N');
      } catch (error) {
        vueData.serverError(error, 'Error al cargar puntos de venta');
        console.error(error);
        this.salesPoints = [];
      } finally {
      }
    }

    @Watch('invoicingData', { immediate: true })
    onInvoicingDataChanged() {
      if (this.invoicingData) {
        this.getSalesPoints();
      }
    }

    async saveSalesPoint() {
      if (!this.selectedSalesPoint) return;

      try {
        this.updating = true;
        this.invoicingData = this.normalizeInvoicingData(
          await InvoicingAfipService.update({
            invoicingDataId: this.invoicingData.id,
            nroSalesPoint: this.selectedSalesPoint.Nro,
            businessCategory: this.invoicingData.data.businessCategory,
          })
        );
      } catch (error) {
        vueData.serverError(error, 'Error al guardar punto de venta');
        console.error(error);
      } finally {
        this.updating = false;
      }
    }

    async update() {
      try {
        this.updating = true;
        await InvoicingAfipService.update({
          invoicingDataId: this.invoicingData.id,
          name: this.invoicingData.data.name,
          nroSalesPoint: this.invoicingData.data.nroSalesPoint,
          businessCategory: this.invoicingData.data.businessCategory,
          autoInvoice: this.invoicingData.data.autoInvoice,
          // Empty string means "clear the field" (API convention)
          ingresosBrutos: this.invoicingData.data.ingresosBrutos ?? '',
          inicioActividades: this.invoicingData.data.inicioActividades ?? '',
        });
        vueData.addAlert(
          'Datos de facturacion actualizados',
          AlertType.Success
        );
      } catch (error) {
        vueData.serverError(error, 'Error al guardar datos de facturacion');
        console.error(error);
      } finally {
        this.updating = false;
      }
    }

    async remove() {
      const res = await vueData.showConfirmDialog(
        this.$modal,
        '¿Está seguro?',
        'Esta acción no se puede deshacer.',
        'Entiendo, quitar de todos modos'
      );
      if (!res) return;

      try {
        this.removing = true;
        await InvoicingDataService.deleteData(this.invoicingData.id);
        this.invoicingData = null;
      } catch (error) {
        vueData.serverError(error, 'Error al eliminar datos de facturacion');
        console.error(error);
      } finally {
        this.removing = false;
      }
    }
  }
</script>

<template>
  <v-layout class="restaurant-invoicing-container">
    <v-container fluid>
      <v-card>
        <v-card-title>
          <span>Facturacion</span>
          <v-spacer></v-spacer>
        </v-card-title>
        <v-card-subtitle>
          La facturacion electronica le permite a su restaurante emitir facturas
          y tickets fiscales AFIP
        </v-card-subtitle>
        <v-card-text>
          <template v-if="status === 'loading'">
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </template>
          <template v-else-if="!fullyConfigured">
            <v-stepper v-model="step">
              <v-stepper-header>
                <v-stepper-step :complete="step > 1" step="1">
                  Autorización
                </v-stepper-step>
                <v-divider></v-divider>
                <v-stepper-step :complete="step > 2" step="2">
                  Punto de venta
                </v-stepper-step>
              </v-stepper-header>
              <v-stepper-items>
                <v-stepper-content step="1">
                  <InvoicingSetupProgress
                    v-if="setupJobPublicId"
                    :key="setupJobPublicId"
                    :job-public-id="setupJobPublicId"
                    :restaurant-id="restaurant.id"
                    @succeeded="onSetupSucceeded"
                    @retry="onSetupRetry"
                  />
                  <template v-else>
                    <p>
                      Para crear el certificado digital y la autorización que
                      nos permitirá generar facturas electrónicas en su nombre,
                      necesitamos los siguientes datos.
                      <br />
                      <strong
                        >Antes de continuar, habilite en ARCA el servicio
                        «Administración de Certificados Digitales»:
                        Administrador de Relaciones de Clave Fiscal (si es una
                        sociedad, elíjala) › Adherir servicio › ARCA › Servicios
                        interactivos › Administración de Certificados
                        Digitales.</strong
                      >
                    </p>
                    <v-form @submit.prevent>
                      <v-text-field
                        v-model="setupData.name"
                        label="Nombre o Razón Social"
                        required
                        :disabled="creating"
                      ></v-text-field>
                      <v-text-field
                        v-model="setupData.cuit"
                        label="CUIT"
                        required
                        hint="CUIT del titular a nombre del cual se emitirán las facturas"
                        persistent-hint
                        :disabled="creating"
                      ></v-text-field>
                      <v-text-field
                        v-model="setupData.cuitAdministrador"
                        label="CUIT Administrador (opcional)"
                        hint="Si se trata de una sociedad, el CUIT del administrador"
                        persistent-hint
                        :disabled="creating"
                      ></v-text-field>
                      <v-text-field
                        v-model="setupData.password"
                        label="Clave Fiscal"
                        required
                        hint="Clave fiscal nivel 3. La misma no se almacena en nuestros servidores, solo se utiliza para generar el certificado digital"
                        persistent-hint
                        :disabled="creating"
                      ></v-text-field>
                      <div
                        class="text-center tw-flex tw-flex-col mt-4 tw-space-y-2 tw-items-center"
                      >
                        <v-btn
                          color="primary"
                          @click="create"
                          :loading="creating"
                          >Crear autorización</v-btn
                        >
                        <b>El proceso puede tardar varios minutos.</b>
                      </div>
                    </v-form>
                  </template>
                </v-stepper-content>
                <v-stepper-content step="2">
                  <p>
                    Para poder facturar, debe crear un Punto de Venta en AFIP
                    especial para QueResto. Puede hacerlo siguiendo las
                    instrucciones detalladas en
                    <a
                      href="https://docs.afipsdk.com/recursos/tutoriales-pagina-de-arca/crear-punto-de-venta"
                      target="_blank"
                      >este enlace</a
                    >
                  </p>
                  <!-- Instrucciones TODO... -->
                  <h4>
                    Una vez que haya creado el Punto de Venta, selecciónelo a
                    continuación.
                  </h4>
                  <p>Si no ve el nuevo Punto de Venta, presione Actualizar</p>
                  <template v-if="!salesPoints">
                    <v-progress-linear
                      indeterminate
                      color="primary"
                    ></v-progress-linear>
                  </template>
                  <template v-else>
                    <v-form @submit.prevent>
                      <div class="tw-flex tw-space-x-2 tw-items-center">
                        <v-select
                          v-model="selectedSalesPoint"
                          :items="salesPoints"
                          item-value="Nro"
                          label="Punto de Venta"
                          return-object
                          required
                        >
                          <template v-slot:selection="data">
                            {{ data.item.Nro }} - {{ data.item.EmisionTipo }}
                          </template>
                          <template v-slot:item="data">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ data.item.Nro }} -
                                {{ data.item.EmisionTipo }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </template>
                        </v-select>
                        <v-btn color="primary" @click="getSalesPoints"
                          >Actualizar</v-btn
                        >
                      </div>
                      <div class="text-center tw-mt-4">
                        <v-btn color="primary" @click="saveSalesPoint"
                          >Guardar</v-btn
                        >
                      </div>
                    </v-form>
                  </template>
                </v-stepper-content>
              </v-stepper-items>
            </v-stepper>
          </template>
          <templave v-else>
            <p>Facturacion configurada correctamente</p>
            <h4>Datos de facturacion</h4>
            <v-form @submit.prevent>
              <v-text-field
                v-model="invoicingData.data.name"
                label="Nombre o Razón Social"
                required
                :disabled="updating || removing"
              ></v-text-field>
              <v-text-field
                v-model="invoicingData.data.cuit"
                label="CUIT"
                required
                disabled
              ></v-text-field>
              <v-text-field
                v-model="invoicingData.data.cuitAdministrador"
                label="CUIT Administrador (opcional)"
                disabled
              ></v-text-field>
              <template v-if="!salesPoints">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </template>
              <template v-else>
                <v-select
                  v-model="invoicingData.data.nroSalesPoint"
                  :items="salesPoints"
                  item-value="Nro"
                  label="Punto de Venta"
                  required
                  :disabled="updating || removing"
                >
                  <template v-slot:selection="data">
                    {{ data.item.Nro }} - {{ data.item.EmisionTipo }}
                  </template>
                  <template v-slot:item="data">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ data.item.Nro }} - {{ data.item.EmisionTipo }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-select>
              </template>
              <v-select
                v-model="invoicingData.data.businessCategory"
                :items="businessCategories"
                label="Categoria de IVA"
                required
                :disabled="updating || removing"
              ></v-select>
              <v-text-field
                v-model="invoicingData.data.ingresosBrutos"
                label="Ingresos Brutos"
                hint="Número de inscripción o condición (ej: Exento)"
                persistent-hint
                :disabled="updating || removing"
              ></v-text-field>
              <v-menu
                v-model="inicioActividadesMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="invoicingData.data.inicioActividades"
                    label="Inicio de Actividades"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    clearable
                    :disabled="updating || removing"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="invoicingData.data.inicioActividades"
                  :locale="$i18n.locale"
                  @input="inicioActividadesMenu = false"
                />
              </v-menu>
              <!-- Facturar automaticamente tras registrar un pago -->
              <div>
                <span>Facturación automatica</span>
                <v-switch
                  v-model="invoicingData.data.autoInvoice"
                  label="Facturar automaticamente tras registrar un pago"
                  class="mt-0"
                  :disabled="updating || removing"
                ></v-switch>
              </div>
              <div
                class="mt-4 tw-flex tw-space-x-2 tw-items-center tw-justify-center"
              >
                <v-btn
                  color="primary"
                  @click="update"
                  :disabled="!salesPoints || removing"
                  :loading="updating"
                  >Guardar</v-btn
                >
                <v-btn
                  color="error"
                  @click="remove"
                  text
                  :disabled="updating"
                  :loading="removing"
                  >Eliminar configuracion</v-btn
                >
              </div>
            </v-form>
          </templave>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<style lang="scss" scoped>
  @import '@/assets/styles/titles.scss';
  .restaurant-invoicing-container {
  }
</style>
